import type { Axis } from '@cf/shared'
import { Chessboard } from '@/components/Chessboard'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

export type Board = Axis['boards'][number]
export type Wording = Board['commentaries'][number]
export type AxisHead = Pick<Axis, 'title' | 'poleLeft' | 'poleRight' | 'ordinal'>

/**
 * The wordings of a board, in the order this reviewer meets them.
 *
 * Shuffled per board from its id rather than at random on every render: the
 * order has to be stable for a reviewer coming back to a board, and it has to
 * differ between boards so the first slot does not win by being first. The
 * screen label ("A", "B") is the position; `variant` stays the authored name,
 * and that is what the answer records.
 */
export function orderedWordings(board: Board): Array<Wording & { label: string }> {
  let hash = 7
  for (const character of board.id) hash = (hash * 33 + character.charCodeAt(0)) % 99991
  const wordings = [...board.commentaries]
  if (wordings.length > 1 && hash % 2 === 1) wordings.reverse()
  return wordings.map((wording, index) => ({
    ...wording,
    label: String.fromCharCode(65 + index),
  }))
}

export const OVERLINE =
  'text-[10px] font-semibold uppercase tracking-[0.08em] text-faint'

/** The diagram, the two moves it offers, and the position it came from. */
export function BoardPanel({
  board,
  className,
}: {
  board: Board
  className?: string
}) {
  return (
    <Card className={cn('gap-4 py-4', className)}>
      <CardContent className="flex flex-col gap-3 px-4">
        <Chessboard
          fen={board.fen}
          playedUci={board.played.uci}
          alternativeUci={board.alternative.uci}
        />

        <div
          className="flex flex-col gap-2 text-xs leading-relaxed text-muted-foreground"
          data-testid="move-summaries"
        >
          <div className="flex items-start gap-1.5" data-testid="played-move-summary">
            <span className="mt-1 size-2.5 shrink-0 rounded-[3px] bg-green" />
            <p className="min-w-0 break-words">
              zagrany <span className="font-mono text-foreground">{board.played.san}</span>{' '}
              <span className="font-mono">({board.played.line.join(' ')})</span>
            </p>
          </div>
          <div className="flex items-start gap-1.5" data-testid="alternative-move-summary">
            <span className="mt-1 size-2.5 shrink-0 rounded-[3px] bg-amber" />
            <p className="min-w-0 break-words">
              alternatywa{' '}
              <span className="font-mono text-foreground">{board.alternative.san}</span>{' '}
              <span className="font-mono">({board.alternative.line.join(' ')})</span>
            </p>
          </div>
        </div>

        <Separator />

        <div className="flex flex-col gap-1">
          <span className={OVERLINE}>FEN</span>
          <span className="font-mono text-[11px] leading-relaxed break-all text-muted-foreground">
            {board.fen}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

/** "Oś 1 · dowód 3", then the axis with the played pole lit up. */
export function AxisHeading({
  board,
  axis,
  badge,
}: {
  board: Board
  axis: AxisHead
  /** Optional marker, e.g. that this reviewer already answered. */
  badge?: React.ReactNode
}) {
  const activeLeft = board.played.pole === axis.poleLeft
  const pole = (active: boolean) =>
    cn(
      'box-decoration-clone rounded-md px-2 py-0.5',
      active ? 'bg-accent font-semibold text-accent-foreground' : 'text-faint',
    )

  return (
    <div className="flex flex-col gap-2">
      {/* The move pair rides along with the label, and not uppercased: it is
          how a reviewer matches this board with the one in their PDF. */}
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <span className={OVERLINE}>
          Oś {axis.ordinal} · dowód {board.ordinal}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{board.moveLabel}</span>
        {board.review.flagged ? (
          <Badge tone="amber" title="oznaczony w raporcie jako wątpliwy">
            do rewizji
          </Badge>
        ) : null}
        {badge}
      </div>
      <h2 className="font-serif text-xl leading-relaxed">
        <span className={pole(activeLeft)}>{axis.poleLeft}</span>{' '}
        <span className="text-faint">↔</span>{' '}
        <span className={pole(!activeLeft)}>{axis.poleRight}</span>
      </h2>
    </div>
  )
}

/**
 * The prose card: the move that was played, and the move that was not.
 *
 * `label` is set only when the board carries more than one wording, in which
 * case the card is headed "Wersja A" and whatever the caller puts in
 * `children` (the verdict chips) sits under the text.
 */
export function EvidenceProse({
  board,
  wording,
  label,
  children,
}: {
  board: Board
  wording: Wording
  label?: string
  children?: React.ReactNode
}) {
  return (
    <Card className="gap-0 py-5">
      {label ? (
        <CardHeader className="px-5 pb-3">
          <CardTitle className={OVERLINE}>Wersja {label}</CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className="flex flex-col gap-4 px-5">
        <MoveBlock
          tone="green"
          label="Ruch zawodnika"
          move={board.played.san}
          text={wording.played}
        />
        <Separator />
        <MoveBlock
          tone="amber"
          label="Alternatywa"
          move={board.alternative.san}
          text={wording.alternative}
        />
        {children ? (
          <>
            <Separator />
            {children}
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * The variations the screen report prints under each move are deliberately not
 * shown (owner, 2026-08-03). They travel in the dataset and the API either
 * way, so bringing them back is a matter of rendering them again.
 */
function MoveBlock({
  tone,
  label,
  move,
  text,
}: {
  tone: 'green' | 'amber'
  label: string
  move: string
  text: string
}) {
  return (
    <div className="flex gap-3">
      <span
        className={cn(
          'mt-1.5 w-1 shrink-0 rounded-full',
          tone === 'green' ? 'bg-green' : 'bg-amber',
        )}
      />
      <div className="flex min-w-0 flex-col gap-1">
        <span
          className={cn(
            'text-sm font-semibold',
            tone === 'green' ? 'text-green-dark' : 'text-amber-dark',
          )}
        >
          {label} · <span className="font-mono">{move}</span>
        </span>
        <p className="font-serif text-[16.5px] leading-relaxed text-ink-soft">{text}</p>
      </div>
    </div>
  )
}

/**
 * Board and prose side by side, for the report, where every piece of evidence
 * is read once and scrolled past.
 */
export function EvidenceCard({
  board,
  axis,
  badge,
  children,
}: {
  board: Board
  axis: AxisHead
  badge?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <article className="grid items-start gap-5 lg:grid-cols-[minmax(280px,400px)_1fr]">
      <BoardPanel board={board} />
      <div className="flex flex-col gap-4">
        <AxisHeading board={board} axis={axis} badge={badge} />
        {orderedWordings(board).map((wording) => (
          <EvidenceProse
            key={wording.variant}
            board={board}
            wording={wording}
            label={board.commentaries.length > 1 ? wording.label : undefined}
          />
        ))}
        {children}
      </div>
    </article>
  )
}
